import $ from 'jquery';
import { initTooltips } from '@/js/modules/tooltips';

// The discussion panel's message rendering, shared by the discussion setup in
// application/messages.js and the messages_controller that receives messages over ActionCable.
// Nothing runs at import; the panel and the current user are looked up when a function runs.
const discussionPanel = () => $('.discussion-panel').first();
const currentUserId = () => $('meta[name="user-id"]').attr('content');

export function resetMessageInputs($form) {
  const $input = $form.find('.tiptap');
  const $fileUploadField = $form.find('.file-uploader-field--discussion-message');

  resetSendingStateMessage();

  $form
    .find('button[type="submit"]')
    .removeClass('button-loading')
    .addClass('button-disabled')
    .attr('disabled', true);

  $fileUploadField.data('files', []);
  $fileUploadField.val('');
  $form.find('.file-preview-link').remove();
  $form.closest('.file-uploader-drop').removeClass('file-uploader-drop--disabled');
  $form.find('.t-button--attach-a-file').removeClass('button-disabled').attr('disabled', false);
  $form
    .closest('.discussion-panel__form-container')
    .removeClass('discussion-panel__form-container--editing');

  $form.find('input[name="message[text]"]').val('');
  $form.find('#update_message_id').val('');
  $form.find('input[name="user_file_ids"]').val('');

  $input
    .removeClass('text-editor--disabled')
    .attr('contenteditable', 'true')
    .addClass('resetRequest')
    .focus();
}

function resetSendingStateMessage() {
  const $discussionPanel = discussionPanel();
  const $sendingMessage = $discussionPanel.find('.discussion-panel__sending-message');
  const $attachment = $sendingMessage.find('.discussion-message__attachment');

  $attachment.not(':first').remove();
  $attachment.addClass('hide');
  $sendingMessage.find('.discussion-message__board').addClass('hide');
  $sendingMessage.find('.discussion-message__shoppable').addClass('hide');
  $sendingMessage
    .find('.discussion-message')
    .removeClass('discussion-message--board')
    .removeData('board-id');
  $sendingMessage.find('.discussion-message__message').addClass('hide').empty();
}

export function validateMessageBeforeShow(messageId) {
  const $message = $(`.discussion-message[data-message-id="${messageId}"]`);
  const $messagesContainer = $message.closest('.discussion-panel__messages-container');
  if (!$message.length || !$messagesContainer.length) {
    return;
  }

  const $sendingMessage = $messagesContainer.find('.discussion-message--sending');
  $sendingMessage.remove();
  toggleMessageAuthorClass($message);
  filterMessageByThread($message);
  divideMessagesContainerByDate($messagesContainer);
  initTooltips($messagesContainer.find('.tooltip'));
}

export function toggleMessageAuthorClass($message) {
  const senderId = $message.attr('data-sender-id');
  const adminAdopted = $message.attr('data-admin-adopted');

  if (adminAdopted) {
    $message.addClass('discussion-message--admin-adopted');
    return;
  }

  if (senderId && senderId === currentUserId()) {
    $message.addClass('discussion-message--author');
  }
}

export function filterMessageByThread($message) {
  const $discussionPanel = discussionPanel();
  const boardThread = $discussionPanel.data('boardThread');
  const boardId = $discussionPanel.data('boardId');
  const shoppableThread = $discussionPanel.data('shoppableThread');
  const shoppableId = $discussionPanel.data('shoppableId');

  if (!$discussionPanel.is('.discussion-panel--board, .discussion-panel--shoppable')) {
    $message.removeClass('hide');
    return;
  }

  const isBoardMatch =
    String($message.data('boardThread')) === String(boardThread) &&
    String($message.data('boardId')) === String(boardId) &&
    $message.hasClass('discussion-message--board') &&
    $discussionPanel.hasClass('discussion-panel--board');

  const isShoppableMatch =
    String($message.data('boardThread')) === String(boardThread) &&
    String($message.data('boardId')) === String(boardId) &&
    String($message.data('shoppableThread')) === String(shoppableThread) &&
    String($message.data('shoppableId')) === String(shoppableId) &&
    $message.hasClass('discussion-message--shoppable') &&
    $discussionPanel.hasClass('discussion-panel--shoppable');

  if (isBoardMatch || isShoppableMatch) {
    $message.removeClass('hide');
  } else {
    $message.addClass('hide');
  }
}

export function divideMessagesContainerByDate($messagesContainer) {
  const today = new Date().toISOString().slice(0, 10);
  let lastDate = $messagesContainer.data('last-date') || '';
  const $messages = $messagesContainer
    .find('.discussion-message')
    .not('.hide, .discussion-message--sending');
  const $firstMessage = $messages.first();
  let dividerLabel;

  $messagesContainer.find('.discussion-panel__divider').remove();

  $messages.each(function () {
    const messageDate = $(this).data('message-date');
    const isFirstMessage = $firstMessage.is($(this));

    if (messageDate === today) {
      dividerLabel = 'Today';
    } else {
      const options = { weekday: 'long', month: 'long', day: 'numeric' };
      const readableDate = new Date(messageDate);
      dividerLabel = new Intl.DateTimeFormat('en-GB', options).format(readableDate);
    }

    if (messageDate !== lastDate || isFirstMessage) {
      $messagesContainer
        .find(`.discussion-panel__divider[data-divider-date="${messageDate}"]`)
        .remove();
      $(this).before(
        `<div class="discussion-panel__divider" data-divider-date="${messageDate}">${dividerLabel}</div>`
      );
      lastDate = messageDate;
      $messagesContainer.data('last-date', lastDate);
    }
  });
}

export function deleteMessage(messageId) {
  const $message = $(`.discussion-message[data-message-id="${messageId}"]`);

  if (!$message.hasClass('discussion-message--deleted')) {
    const messageText =
      '<i class="fa-regular fa-ban" aria-hidden="true"></i> This message was deleted.';
    $message.addClass('discussion-message--deleted');
    let $messageBody = $message.find('.discussion-message__message');
    if ($messageBody.length === 0) {
      $messageBody = $('<div class="discussion-message__message"></div>');
      $message.find('.discussion-message__body .discussion-message__details').after($messageBody);
    }
    $messageBody.html(messageText);
    $message.find('.discussion-message__attachment').remove();
    $message.find('.discussion-message__board').remove();
  }

  resetMessageEditing(messageId);
}

export function resetMessageEditing(messageId) {
  const $form = discussionPanel().find('.tiptap').closest('form');
  if ($form.find('#update_message_id').val() === String(messageId)) {
    resetMessageInputs($form);
  }
}

export function updateMessage({ id, message }) {
  const $messageWrapper = $(`.discussion-message[data-message-id="${id}"]`);
  const $messageContent = $(message).html();

  if ($messageWrapper.length) {
    $messageWrapper.html($messageContent);
  }
  initTooltips($messageWrapper.find('.tooltip'));
}
